import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import {
  BookOpen, Heart, Sun, Moon, Utensils, Car, Shield, Home,
  Baby, Briefcase, HeartHandshake, CloudRain, Search, Star,
  ChevronDown, ChevronUp, Volume2, Copy, Check
} from 'lucide-react';
import { supabase } from '../lib/supabase';

// Comprehensive Dua Collection
const DUA_CATEGORIES = [
  { id: 'morning', icon: Sun, label_en: 'Morning', label_hr: 'Jutarnje', color: 'amber' },
  { id: 'evening', icon: Moon, label_en: 'Evening', label_hr: 'Večernje', color: 'indigo' },
  { id: 'food', icon: Utensils, label_en: 'Food & Drink', label_hr: 'Hrana i piće', color: 'green' },
  { id: 'travel', icon: Car, label_en: 'Travel', label_hr: 'Putovanje', color: 'blue' },
  { id: 'protection', icon: Shield, label_en: 'Protection', label_hr: 'Zaštita', color: 'red' },
  { id: 'home', icon: Home, label_en: 'Home & Family', label_hr: 'Dom i obitelj', color: 'rose' },
  { id: 'health', icon: HeartHandshake, label_en: 'Health', label_hr: 'Zdravlje', color: 'emerald' },
  { id: 'general', icon: Star, label_en: 'General', label_hr: 'Opće', color: 'purple' },
];

const DUAS = [
  // Morning Duas
  {
    id: 1,
    category: 'morning',
    title_en: 'Upon Waking Up',
    title_hr: 'Pri buđenju',
    arabic: 'الْحَمْدُ لِلَّهِ الَّذِي أَحْيَانَا بَعْدَ مَا أَمَاتَنَا وَإِلَيْهِ النُّشُورُ',
    transliteration: 'Alhamdulillahil-ladhi ahyana ba\'da ma amatana wa ilayhin-nushur',
    translation_en: 'All praise is for Allah who gave us life after having taken it from us and unto Him is the resurrection.',
    translation_hr: 'Hvala Allahu koji nas je oživio nakon što nas je usmrtio i Njemu se vraćamo.',
    reference: 'Sahih al-Bukhari'
  },
  {
    id: 2,
    category: 'morning',
    title_en: 'Morning Remembrance',
    title_hr: 'Jutarnji zikr',
    arabic: 'أَصْبَحْنَا وَأَصْبَحَ الْمُلْكُ لِلَّهِ، وَالْحَمْدُ لِلَّهِ، لاَ إِلَـهَ إِلاَّ اللهُ وَحْدَهُ لاَ شَرِيكَ لَهُ',
    transliteration: 'Asbahna wa asbahal-mulku lillah, walhamdu lillah, la ilaha illallahu wahdahu la sharika lah',
    translation_en: 'We have reached the morning and at this very time all sovereignty belongs to Allah. All praise is for Allah. None has the right to be worshipped except Allah, alone, without partner.',
    translation_hr: 'Osvanuli smo i osvanula je vlast Allahova. Hvala Allahu. Nema boga osim Allaha, Jedinog, koji nema sudruga.',
    reference: 'Abu Dawud'
  },
  {
    id: 3,
    category: 'morning',
    title_en: 'Sayyid al-Istighfar (Master of Seeking Forgiveness)',
    title_hr: 'Sejjidul-istigfar (Prvak traženja oprosta)',
    arabic: 'اللَّهُمَّ أَنْتَ رَبِّي لا إِلَهَ إِلا أَنْتَ، خَلَقْتَنِي وَأَنَا عَبْدُكَ، وَأَنَا عَلَى عَهْدِكَ وَوَعْدِكَ مَا اسْتَطَعْتُ',
    transliteration: 'Allahumma anta Rabbi la ilaha illa anta, khalaqtani wa ana abduka, wa ana ala ahdika wa wadika mastata\'tu',
    translation_en: 'O Allah, You are my Lord, none has the right to be worshipped except You, You created me and I am Your servant and I abide to Your covenant and promise as best I can.',
    translation_hr: 'Allahu, Ti si moj Gospodar, nema boga osim Tebe, stvorio si me i ja sam Tvoj rob, i držim se Tvog zavjeta i obećanja koliko mogu.',
    reference: 'Sahih al-Bukhari'
  },
  // Evening Duas
  {
    id: 4,
    category: 'evening',
    title_en: 'Evening Remembrance',
    title_hr: 'Večernji zikr',
    arabic: 'أَمْسَيْنَا وَأَمْسَى الْمُلْكُ للهِ، وَالْحَمْدُ للهِ، لَا إِلَهَ إِلاَّ اللهُ وَحْدَهُ لَا شَرِيكَ لَهُ',
    transliteration: 'Amsayna wa amsal-mulku lillah, walhamdu lillah, la ilaha illallahu wahdahu la sharika lah',
    translation_en: 'We have reached the evening and at this very time all sovereignty belongs to Allah. All praise is for Allah. None has the right to be worshipped except Allah, alone, without partner.',
    translation_hr: 'Omrkli smo i omrkla je vlast Allahova. Hvala Allahu. Nema boga osim Allaha, Jedinog, koji nema sudruga.',
    reference: 'Abu Dawud'
  },
  {
    id: 5,
    category: 'evening',
    title_en: 'Before Sleeping',
    title_hr: 'Prije spavanja',
    arabic: 'بِاسْمِكَ اللَّهُمَّ أَمُوتُ وَأَحْيَا',
    transliteration: 'Bismika Allahumma amutu wa ahya',
    translation_en: 'In Your name O Allah, I live and die.',
    translation_hr: 'U Tvoje ime, Allahu, umirem i živim.',
    reference: 'Sahih al-Bukhari'
  },
  {
    id: 6,
    category: 'evening',
    title_en: 'Ayatul Kursi Before Sleep',
    title_hr: 'Ajetul-Kursi prije spavanja',
    arabic: 'اللَّهُ لاَ إِلَهَ إِلاَّ هُوَ الْحَيُّ الْقَيُّومُ لاَ تَأْخُذُهُ سِنَةٌ وَلاَ نَوْمٌ',
    transliteration: 'Allahu la ilaha illa huwal-Hayyul-Qayyum, la ta\'khudhuhu sinatun wa la nawm...',
    translation_en: 'Allah - there is no deity except Him, the Ever-Living, the Sustainer of existence. Neither drowsiness overtakes Him nor sleep...',
    translation_hr: 'Allah je - nema boga osim Njega - Živi i Vječni! Ne obuzima Ga ni drijemež ni san...',
    reference: 'Quran 2:255'
  },
  // Food & Drink
  {
    id: 7,
    category: 'food',
    title_en: 'Before Eating',
    title_hr: 'Prije jela',
    arabic: 'بِسْمِ اللهِ',
    transliteration: 'Bismillah',
    translation_en: 'In the name of Allah.',
    translation_hr: 'U ime Allaha.',
    reference: 'Abu Dawud'
  },
  {
    id: 8,
    category: 'food',
    title_en: 'After Eating',
    title_hr: 'Poslije jela',
    arabic: 'الْحَمْدُ لِلَّهِ الَّذِي أَطْعَمَنِي هَذَا، وَرَزَقَنِيهِ، مِنْ غَيْرِ حَوْلٍ مِنِّي وَلاَ قُوَّةٍ',
    transliteration: 'Alhamdu lillahil-ladhi at\'amani hadha, wa razaqanihi, min ghayri hawlin minni wa la quwwah',
    translation_en: 'All praise is for Allah who fed me this and provided it for me without any might or power from myself.',
    translation_hr: 'Hvala Allahu koji me nahranio ovim i opskrbio me time bez ikakve moje moći i snage.',
    reference: 'Abu Dawud, At-Tirmidhi'
  },
  {
    id: 9,
    category: 'food',
    title_en: 'When Breaking Fast',
    title_hr: 'Pri iftaru',
    arabic: 'ذَهَبَ الظَّمَأُ وَابْتَلَّتِ الْعُرُوقُ وَثَبَتَ الْأَجْرُ إِنْ شَاءَ اللَّهُ',
    transliteration: 'Dhahaba adh-dhama\' wabtallatil-\'uruq wa thabatal-ajru insha\'Allah',
    translation_en: 'The thirst has gone and the veins are moistened, and the reward is confirmed, if Allah wills.',
    translation_hr: 'Žeđ je otišla, žile su se navlažile i nagrada je osigurana, ako Allah htjedne.',
    reference: 'Abu Dawud'
  },
  // Travel
  {
    id: 10,
    category: 'travel',
    title_en: 'When Starting a Journey',
    title_hr: 'Pri polasku na put',
    arabic: 'سُبْحَانَ الَّذِي سَخَّرَ لَنَا هَذَا وَمَا كُنَّا لَهُ مُقْرِنِينَ وَإِنَّا إِلَى رَبِّنَا لَمُنْقَلِبُونَ',
    transliteration: 'Subhanal-ladhi sakhkhara lana hadha wa ma kunna lahu muqrinin, wa inna ila Rabbina lamunqalibun',
    translation_en: 'Glory to Him who has subjected this to us, and we could never have it. And indeed we, to our Lord, will surely return.',
    translation_hr: 'Slavljen neka je Onaj koji nam je ovo potčinio, a mi to sami ne bismo mogli postići. I mi ćemo se sigurno Gospodaru svome vratiti.',
    reference: 'Quran 43:13-14'
  },
  {
    id: 11,
    category: 'travel',
    title_en: 'When Entering a Vehicle',
    title_hr: 'Pri ulasku u vozilo',
    arabic: 'بِسْمِ اللهِ، تَوَكَّلْتُ عَلَى اللهِ، لاَ حَوْلَ وَلاَ قُوَّةَ إِلاَّ بِاللهِ',
    transliteration: 'Bismillah, tawakkaltu alallah, la hawla wa la quwwata illa billah',
    translation_en: 'In the name of Allah. I place my trust in Allah. There is no might and no power except with Allah.',
    translation_hr: 'U ime Allaha. Oslanjam se na Allaha. Nema snage ni moći osim s Allahom.',
    reference: 'Abu Dawud, At-Tirmidhi'
  },
  // Protection
  {
    id: 12,
    category: 'protection',
    title_en: 'For Protection',
    title_hr: 'Za zaštitu',
    arabic: 'بِسْمِ اللهِ الَّذِي لاَ يَضُرُّ مَعَ اسْمِهِ شَيْءٌ فِي الأَرْضِ وَلاَ فِي السَّمَاءِ وَهُوَ السَّمِيعُ الْعَلِيمُ',
    transliteration: 'Bismillahil-ladhi la yadurru ma\'asmihi shay\'un fil-ardi wa la fis-sama\'i wa huwas-Sami\'ul-\'Alim',
    translation_en: 'In the name of Allah with whose name nothing can harm on earth or in heaven, and He is the All-Hearing, the All-Knowing.',
    translation_hr: 'U ime Allaha, s čijim imenom ništa ne može nauditi na zemlji niti na nebu, a On je Svečujući, Sveznajući.',
    reference: 'Abu Dawud, At-Tirmidhi'
  },
  {
    id: 13,
    category: 'protection',
    title_en: 'Against Evil Eye',
    title_hr: 'Protiv uroka',
    arabic: 'أَعُوذُ بِكَلِمَاتِ اللهِ التَّامَّةِ مِنْ كُلِّ شَيْطَانٍ وَهَامَّةٍ، وَمِنْ كُلِّ عَيْنٍ لَامَّةٍ',
    transliteration: 'A\'udhu bikalimatillahit-tammati min kulli shaytanin wa hammah, wa min kulli \'aynin lammah',
    translation_en: 'I seek refuge in the perfect words of Allah from every devil and poisonous creature and from every evil eye.',
    translation_hr: 'Utječem se savršenim Allahovim riječima od svakog šejtana i otrovnice, i od svakog urokljivog oka.',
    reference: 'Sahih al-Bukhari'
  },
  // Home & Family
  {
    id: 14,
    category: 'home',
    title_en: 'When Entering Home',
    title_hr: 'Pri ulasku u kuću',
    arabic: 'بِسْمِ اللهِ وَلَجْنَا، وَبِسْمِ اللهِ خَرَجْنَا، وَعَلَى اللهِ رَبِّنَا تَوَكَّلْنَا',
    transliteration: 'Bismillahi walajna, wa bismillahi kharajna, wa \'alallahi Rabbina tawakkalna',
    translation_en: 'In the name of Allah we enter and in the name of Allah we leave, and upon our Lord we place our trust.',
    translation_hr: 'U ime Allaha ulazimo i u ime Allaha izlazimo, i na Allaha, Gospodara našeg, oslanjamo se.',
    reference: 'Abu Dawud'
  },
  {
    id: 15,
    category: 'home',
    title_en: 'For Newlyweds',
    title_hr: 'Za mladence',
    arabic: 'بَارَكَ اللهُ لَكَ، وَبَارَكَ عَلَيْكَ، وَجَمَعَ بَيْنَكُمَا فِي خَيْرٍ',
    transliteration: 'Barakallahu lak, wa baraka \'alayk, wa jama\'a baynakuma fi khayr',
    translation_en: 'May Allah bless you, and shower His blessings upon you, and join you together in goodness.',
    translation_hr: 'Allah te blagoslovio i obasuo te Svojim blagoslovima i spojio vas dvoje u dobru.',
    reference: 'Abu Dawud, At-Tirmidhi'
  },
  // Health
  {
    id: 16,
    category: 'health',
    title_en: 'When Visiting the Sick',
    title_hr: 'Pri posjeti bolesniku',
    arabic: 'لاَ بَأْسَ طَهُورٌ إِنْ شَاءَ اللهُ',
    transliteration: 'La ba\'sa, tahurun insha\'Allah',
    translation_en: 'No worry, it is a purification, if Allah wills.',
    translation_hr: 'Nema problema, to je čišćenje, ako Allah htjedne.',
    reference: 'Sahih al-Bukhari'
  },
  {
    id: 17,
    category: 'health',
    title_en: 'For Healing',
    title_hr: 'Za ozdravljenje',
    arabic: 'اللَّهُمَّ رَبَّ النَّاسِ أَذْهِبِ الْبَأْسَ، اشْفِهِ وَأَنْتَ الشَّافِي، لاَ شِفَاءَ إِلاَّ شِفَاؤُكَ، شِفَاءً لاَ يُغَادِرُ سَقَمَاً',
    transliteration: 'Allahumma Rabban-nas, adhhibil-ba\'s, ishfihi wa antas-Shafi, la shifa\'a illa shifa\'uk, shifa\'an la yughadiru saqama',
    translation_en: 'O Allah, Lord of mankind, remove the affliction and cure him. You are the One who cures, there is no cure except Your cure, a cure that leaves no disease.',
    translation_hr: 'Allahu, Gospodaru ljudi, ukloni nevolju i izliječi ga. Ti si Onaj koji liječi, nema lijeka osim Tvog lijeka, lijeka koji ne ostavlja bolest.',
    reference: 'Sahih al-Bukhari, Muslim'
  },
  // General
  {
    id: 18,
    category: 'general',
    title_en: 'Istikhara (Seeking Guidance)',
    title_hr: 'Istihara (traženje upute)',
    arabic: 'اللَّهُمَّ إِنِّي أَسْتَخِيرُكَ بِعِلْمِكَ، وَأَسْتَقْدِرُكَ بِقُدْرَتِكَ، وَأَسْأَلُكَ مِنْ فَضْلِكَ الْعَظِيمِ',
    transliteration: 'Allahumma inni astakhiruka bi\'ilmik, wa astaqdiruka biqudratik, wa as\'aluka min fadlikal-\'azim',
    translation_en: 'O Allah, I seek Your guidance by virtue of Your knowledge, and I seek ability by virtue of Your power, and I ask You of Your great bounty.',
    translation_hr: 'Allahu, tražim Tvoju uputu putem Tvog znanja, i tražim sposobnost putem Tvoje moći, i molim Te za Tvoju veliku dobrotu.',
    reference: 'Sahih al-Bukhari'
  },
  {
    id: 19,
    category: 'general',
    title_en: 'For Forgiveness',
    title_hr: 'Za oprost',
    arabic: 'أَسْتَغْفِرُ اللهَ الْعَظِيمَ الَّذِي لاَ إِلَهَ إِلاَّ هُوَ الْحَيُّ الْقَيُّومُ وَأَتُوبُ إِلَيْهِ',
    transliteration: 'Astaghfirullaha al-\'Azim alladhi la ilaha illa huwal-Hayyul-Qayyum wa atubu ilayh',
    translation_en: 'I seek forgiveness from Allah, the Magnificent, whom there is none worthy of worship except Him, the Living, the Sustainer, and I repent unto Him.',
    translation_hr: 'Tražim oprost od Allaha, Veličanstvenog, osim Kojeg nema drugog boga, Živog, Vječnog, i Njemu se kajem.',
    reference: 'Abu Dawud, At-Tirmidhi'
  },
  {
    id: 20,
    category: 'general',
    title_en: 'For Anxiety and Sorrow',
    title_hr: 'Za tjeskobu i tugu',
    arabic: 'اللَّهُمَّ إِنِّي أَعُوذُ بِكَ مِنَ الْهَمِّ وَالْحَزَنِ، وَالْعَجْزِ وَالْكَسَلِ',
    transliteration: 'Allahumma inni a\'udhu bika minal-hammi wal-hazan, wal-\'ajzi wal-kasal',
    translation_en: 'O Allah, I seek refuge in You from anxiety and sorrow, weakness and laziness.',
    translation_hr: 'Allahu, utječem Ti se od brige i tuge, od nemoći i lijenosti.',
    reference: 'Sahih al-Bukhari'
  },
];

function DuaCard({ dua, isFavorite, onToggleFavorite, lang }) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(dua.arabic);
      setCopied(true);
      toast.success(lang === 'hr' ? 'Kopirano!' : 'Copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error(lang === 'hr' ? 'Greška pri kopiranju' : 'Failed to copy');
    }
  };

  return (
    <div className="bg-white rounded-xl border border-stone-200 overflow-hidden transition-all hover:shadow-md">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between text-left hover:bg-stone-50 transition-colors"
      >
        <div className="flex-1">
          <h3 className="font-semibold text-[#292524]">
            {lang === 'hr' ? dua.title_hr : dua.title_en}
          </h3>
          <p className="text-xs text-stone-500 mt-1">{dua.reference}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(dua.id);
            }}
            className={`p-2 rounded-lg transition-colors ${
              isFavorite ? 'text-red-500 bg-red-50' : 'text-stone-400 hover:text-red-500 hover:bg-red-50'
            }`}
          >
            <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
          </button>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-stone-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-stone-400" />
          )}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-stone-100">
          {/* Arabic */}
          <div className="mt-4 p-4 bg-emerald-50 rounded-xl">
            <p className="text-xl text-right font-arabic text-[#292524] leading-loose" dir="rtl">
              {dua.arabic}
            </p>
          </div>

          {/* Transliteration */}
          <div className="mt-3">
            <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">
              {lang === 'hr' ? 'Transliteracija' : 'Transliteration'}
            </p>
            <p className="text-sm text-[#57534E] italic">{dua.transliteration}</p>
          </div>

          {/* Translation */}
          <div className="mt-3">
            <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">
              {lang === 'hr' ? 'Prijevod' : 'Translation'}
            </p>
            <p className="text-sm text-[#292524]">
              {lang === 'hr' ? dua.translation_hr : dua.translation_en}
            </p>
          </div>

          {/* Actions */}
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleCopy}
              className="flex items-center gap-2 px-3 py-2 bg-stone-100 text-stone-700 rounded-lg text-sm hover:bg-stone-200 transition-colors"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {lang === 'hr' ? 'Kopiraj arapski' : 'Copy Arabic'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Dua({ user }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [favorites, setFavorites] = useState([]);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  useEffect(() => {
    loadFavorites();
  }, []);

  const loadFavorites = async () => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) return;

      const { data } = await supabase
        .from('dua_favorites')
        .select('dua_id')
        .eq('user_id', currentUser.id);

      if (data) {
        setFavorites(data.map(f => f.dua_id));
      }
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  };

  const toggleFavorite = async (duaId) => {
    try {
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (!currentUser) {
        toast.error(lang === 'hr' ? 'Morate biti prijavljeni' : 'You must be logged in');
        return;
      }

      const isFavorite = favorites.includes(duaId);

      if (isFavorite) {
        await supabase
          .from('dua_favorites')
          .delete()
          .eq('user_id', currentUser.id)
          .eq('dua_id', duaId);
        setFavorites(favorites.filter(id => id !== duaId));
        toast.success(lang === 'hr' ? 'Uklonjeno iz favorita' : 'Removed from favorites');
      } else {
        await supabase
          .from('dua_favorites')
          .insert({ user_id: currentUser.id, dua_id: duaId });
        setFavorites([...favorites, duaId]);
        toast.success(lang === 'hr' ? 'Dodano u favorite' : 'Added to favorites');
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Still update local state even if DB fails
      const isFavorite = favorites.includes(duaId);
      if (isFavorite) {
        setFavorites(favorites.filter(id => id !== duaId));
      } else {
        setFavorites([...favorites, duaId]);
      }
    }
  };

  const filteredDuas = DUAS.filter(dua => {
    // Filter by category
    if (selectedCategory !== 'all' && dua.category !== selectedCategory) {
      return false;
    }

    // Filter by favorites
    if (showFavoritesOnly && !favorites.includes(dua.id)) {
      return false;
    }

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        dua.title_en.toLowerCase().includes(query) ||
        dua.title_hr.toLowerCase().includes(query) ||
        dua.transliteration.toLowerCase().includes(query) ||
        dua.translation_en.toLowerCase().includes(query)
      );
    }

    return true;
  });

  return (
    <Layout user={user}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#292524]">
              {lang === 'hr' ? 'Zbirka Dova' : 'Dua Collection'}
            </h1>
            <p className="text-[#57534E] mt-1">
              {lang === 'hr' ? 'Dove za svaku priliku' : 'Supplications for every occasion'}
            </p>
          </div>
          
          <button
            onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-colors ${
              showFavoritesOnly 
                ? 'bg-red-100 text-red-700 border border-red-200' 
                : 'bg-stone-100 text-stone-700 border border-stone-200'
            }`}
          >
            <Heart className={`w-5 h-5 ${showFavoritesOnly ? 'fill-current' : ''}`} />
            {lang === 'hr' ? 'Favoriti' : 'Favorites'}
            {favorites.length > 0 && (
              <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                {favorites.length}
              </span>
            )}
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-stone-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={lang === 'hr' ? 'Pretraži dove...' : 'Search duas...'}
            className="w-full pl-12 pr-4 py-3 bg-white border border-stone-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
          />
        </div>

        {/* Categories */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
              selectedCategory === 'all'
                ? 'bg-primary text-white'
                : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
            }`}
          >
            {lang === 'hr' ? 'Sve' : 'All'}
          </button>
          {DUA_CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                  selectedCategory === cat.id
                    ? `bg-${cat.color}-100 text-${cat.color}-700 border border-${cat.color}-200`
                    : 'bg-stone-100 text-stone-700 hover:bg-stone-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                {lang === 'hr' ? cat.label_hr : cat.label_en}
              </button>
            );
          })}
        </div>

        {/* Duas List */}
        <div className="space-y-3">
          {filteredDuas.length > 0 ? (
            filteredDuas.map((dua) => (
              <DuaCard
                key={dua.id}
                dua={dua}
                isFavorite={favorites.includes(dua.id)}
                onToggleFavorite={toggleFavorite}
                lang={lang}
              />
            ))
          ) : (
            <div className="text-center py-12 text-stone-500">
              <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{lang === 'hr' ? 'Nema pronađenih dova' : 'No duas found'}</p>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="bg-stone-50 rounded-xl p-4 text-center text-sm text-stone-600">
          {lang === 'hr' 
            ? `${filteredDuas.length} od ${DUAS.length} dova prikazano`
            : `${filteredDuas.length} of ${DUAS.length} duas shown`}
        </div>
      </div>
    </Layout>
  );
}
